import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Badge, Button, EmptyState, InlineError, Input, Skeleton } from '../../components/ui';
import { JobStageBar } from '../supplierExchange/JobStageBar';
import { SubmissionDialog } from '../supplierExchange/SupplierExchangePage';
import { SampleApproval } from '../supplierExchange/SampleApproval';
import { SupplierSampleSlots } from '../supplierExchange/SupplierSampleSlots';
import {
  useAcknowledgeHandoff,
  useHandoffLinks,
  useHandoffs,
  useSubmissions,
} from '../supplierExchange/hooks';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { CertificateLink } from './CertificateLink';
import {
  linkErrorMessage,
  useCreateProjectForOrder,
  useLinkOrderToProject,
  useLinkableProjects,
} from './orderProjectLink';
import { ReviewLinkPanel } from './CgOrdersPage';
import { clientApprovalState } from './financialGate';
import { PoHistory } from './PoHistory';
import { StageTimeline } from './StageTimeline';
import { jobSteps } from './jobStages';
import { describeActivity, sortActivity } from './orderActivity';
import { todayNow } from './sla';
import { trackingLabel, trackingLink } from './tracking.link';
import { FinancialGatePanel } from './FinancialGatePanel';
import { SendPoDialog } from './SendPoDialog';
import { SupplierBill } from './SupplierBill';
import { revisionOutstanding, revisionSummary } from './supplierRevision';
import { slaRemaining } from './sla';
import {
  useAcknowledgeSupplierRevision,
  useDeleteCgOrder,
  useMarkDelivered,
  useOrderClientLogo,
  useSetOrderClientLogo,
  useOrderReferenceFiles,
  useSetOrderReferenceFile,
  useClearOrderReferenceFile,
  useUpdateCgOrder,
} from './hooks';
import { cgErrorMessage, todayIso } from './types';
import { shopifyOrderNoError } from './queries';
import {
  useCertificate,
  useCgItems,
  useCgOrders,
  useCgStatuses,
  useOrderReviewToken,
  useOrderActivity,
  useOrderStageEvents,
  useOrderSupplier,
  usePoDocuments,
  useSetShopifyOrderNo,
} from './hooks';
import type { CgOrder, CgOrderItem } from './types';
import type { ReferenceKind } from './queries';
import type { Certificate } from './CertificateView';
import type { SupplierHandoff, SupplierSubmission } from '../supplierExchange/types';

/**
 * One CG order, everything about it, on its own page.
 *
 * ── Why a page and not the drawer ──────────────────────────────────────────
 *
 * The drawer is an edit form and it is good at that: change three fields and
 * close it. It is the wrong container for the whole history of a job across
 * nine stages — the PO and its revisions, the client's certificate, the design
 * handoff, every sample round, shipping, and the activity feed.
 *
 * Worse, those last two lived on a separate top-level tab called Supplier
 * Reviews, filed by SYSTEM: a screen about handoffs beside a screen about
 * samples beside a screen about stock, none of them beside the order they were
 * all about. Kyu named it — "process wise, should be inside each card as their
 * own sections, so a Job Order shows the full history and activity, in all
 * stages" — which is the same correction the supplier portal already got.
 *
 * The drawer stays. This page links to it, so a quick edit is still quick.
 *
 * ── One shape on both sides ────────────────────────────────────────────────
 *
 * The factory reads a page per job and TAP read a drawer, about the same job.
 * The stage bar here is the SAME derivation the portal renders, with viewer
 * 'tap' — so the two screens cannot describe one order differently.
 */
export function CgOrderPage() {
  const params = useParams<{ orderId: string }>();
  const orderId = params.orderId ?? '';
  const orders = useCgOrders();
  const items = useCgItems();

  if (orders.isPending) {
    return <Skeleton lines={8} label="Loading order" />;
  }
  const order = (orders.data ?? []).find((row) => row.id === orderId);
  if (order === undefined) {
    return (
      <EmptyState
        title="Order not found"
        description="It may have been deleted, or you may not have access to it."
      />
    );
  }

  const orderItems = (items.data ?? []).filter((item) => item.order_id === order.id);
  return <OrderCaseFile order={order} items={orderItems} />;
}

function OrderCaseFile({ order, items }: { order: CgOrder; items: CgOrderItem[] }) {
  const [searchParams] = useSearchParams();
  const itemCount = items.length;
  const supplier = useOrderSupplier(order.id);
  const stages = useOrderStageEvents(order.id);
  const pos = usePoDocuments(order.id);
  const certificate = useCertificate(order.id);
  const activity = useOrderActivity(order.id);
  const submissions = useSubmissions();
  const handoffs = useHandoffs();
  const permissions = useMyPermissions();
  // Opens the review dialog. Whether that dialog offers "Approve for
  // production" is the dialog's own question (canDecideSubmission); this only
  // says whether the person may look and judge options, which any member may.
  const canReview = canEditModule(permissions.data, 'supplier_reviews');
  // Linking is an ordinary CG edit, gated the same way the rest of the page is.
  const canEdit = canEditModule(permissions.data, 'cg_orders');

  const [openSubmission, setOpenSubmission] = useState<SupplierSubmission | null>(null);
  const linkedSampleOpened = useRef(false);
  const [reviewingPo, setReviewingPo] = useState(false);
  const [pageError, setPageError] = useState('');

  // Every sample run made for THIS job. The standalone feed sorted them by
  // date across every job, which is the view that answers no question anybody
  // actually has.
  const runs = (submissions.data ?? []).filter((row) => row.cg_order_id === order.id);
  useEffect(() => {
    const sampleId = searchParams.get('sample');
    if (
      !linkedSampleOpened.current &&
      submissions.data !== undefined &&
      permissions.data !== undefined
    ) {
      linkedSampleOpened.current = true;
      const run = runs.find((row) => row.id === sampleId);
      if (canReview && run !== undefined) setOpenSubmission(run);
    }
  }, [canReview, permissions.data, runs, searchParams, submissions.data]);
  const linked = (handoffs.data ?? []).filter(
    (row) => row.handoff_po_id === order.id && !row.is_order_link,
  );

  const steps = jobSteps(order, { sampleSubmitted: runs.length > 0 });

  return (
    <div className="order-page">
      <div className="order-page-top">
        <Link to="/cg-orders" className="order-page-back">
          ← All orders
        </Link>
      </div>

      <header className="order-page-head">
        <h2 className="order-page-no">Order {order.order_no}</h2>
        <Badge tone="neutral">{order.status}</Badge>
        <span className="order-page-client">{order.client}</span>
      </header>

      {/* The Shopify number, set once Shopify has one.

          Kyu: "We only create the shopify order once the Client approves the
          design." So the number above is a placeholder until somebody puts
          the real one on it, and Ced hit exactly that: Lazzara was #1039 in
          the Hub and #1042 in Shopify with no way to close the gap.

          Offered only while NO PO has been issued. After that the factory
          holds a sheet with the number printed on it and the snapshot behind
          it can never be rewritten, so the database refuses — this hides the
          button rather than letting somebody press it and be told no. */}
      {(pos.data ?? []).length === 0 ? <ShopifyOrderNo order={order} /> : null}

      {/* The same derivation the factory sees, from the same file. Two screens
          that each computed their own would eventually disagree on a phone
          call, which is the failure jobStages.ts exists to prevent. */}
      <JobStageBar steps={steps} viewer="tap" />

      <Section title="The order">
        <dl className="order-page-facts">
          <dt>Client</dt>
          <dd>{order.client}</dd>
          <dt>Supplier</dt>
          <dd>{supplier.data?.organizationName ?? 'Not linked yet'}</dd>
          <dt>Line items</dt>
          <dd>{itemCount === 1 ? '1 item' : `${String(itemCount)} items`}</dd>
          <dt>Target ship date</dt>
          <dd>{order.target_ship_date ?? 'Not committed'}</dd>
          <dt>PO sent</dt>
          <dd>{order.date_po_sent ?? '—'}</dd>
          <dt>Shipped</dt>
          <dd>{order.date_shipped ?? '—'}</dd>
          <dt>Delivered</dt>
          <dd>{order.date_delivered ?? '—'}</dd>
          {order.ship_to.trim() === '' ? null : (
            <>
              <dt>Ship to</dt>
              <dd className="order-page-shipto">{order.ship_to}</dd>
            </>
          )}
        </dl>
      </Section>

      {/* ── The sections below run in the order the work happens ──────────
          "it's almost like every section is the progression of the
          staging/phasing of the order. Chronological so it makes sense."

          Client approval → Design handoff → PO → Samples → Delivery. That is
          the life of a job, and it matches JOB_STEP_ORDER from po_received
          onward; approval and handoff sit ahead of it because both must
          happen before a PO can leave Order Placed.

          The last two are deliberately NOT part of that run. Time in each
          stage is a measurement OF the sequence, and History is the whole log
          — both are things you drop to after reading the story, so they sit
          at the bottom rather than interrupting it. */}

      <Section title="Client approval">
        <ClientApproval
          order={order}
          certificate={certificate.data ?? null}
          onError={setPageError}
        />
      </Section>

      {/* The whole money story in one section, in the order it happens:
          Shopify order raised → invoice sent → invoice paid, then who the PO
          goes to. The send date used to be its own Invoice section above this
          one, which split one chronology across two boxes — Kyu: "Invoice
          sent, in chronological logic is actually in between Shopify Order
          Created and Client Invoice paid." Same panel the drawer and the send
          dialog render, from one file. */}
      <Section title="Financial gate">
        <FinancialGatePanel order={order} onError={setPageError} />
        {pageError === '' ? null : <InlineError>{pageError}</InlineError>}
      </Section>

      {/* Kyu, 2 Sep: the fifth file. Its own section between approval and
          handoff, because that is where it sits in the process — gathered
          from the client, never approved by them, sent to the factory with
          the artwork. */}
      <Section title="Files for the factory">
        <ClientLogoBlock orderId={order.id} />
        {/* Ced, 4 September: the packaging file for Lazzara "had to be sent to
            Jen directly through WhatsApp instead", because this screen had one
            slot and the logo was already in it. Two more, optional, named by
            whoever fills them, and only ever seen by ES. */}
        <ReferenceSlot orderId={order.id} kind="extra_1" ordinal={1} />
        <ReferenceSlot orderId={order.id} kind="extra_2" ordinal={2} />
      </Section>

      <Section title="Design handoff">
        {linked.length === 0 ? (
          <p className="order-page-muted">
            No design handoff is linked to this order. Handoffs are created in Supplier Exchange and
            attached to an order when the PO is generated.
          </p>
        ) : (
          linked.map((handoff) => <HandoffBlock key={handoff.id} handoff={handoff} />)
        )}
      </Section>

      <Section title="Purchase orders">
        {/* Before a PO exists, this section IS the send flow: review the
            actual document, then issue it. After, the in-force sheet renders
            open — "where is the PO" should never need a click to answer. */}
        {order.date_po_sent === null ? (
          <div className="order-page-sendpo">
            <Button onClick={() => setReviewingPo(true)}>Review &amp; send PO</Button>
            <p className="order-page-muted">
              Opens the PO exactly as the factory will receive it. Nothing is sent until you press
              Send inside it.
            </p>
          </div>
        ) : null}
        <PoHistory
          documents={pos.data ?? []}
          isPending={pos.isPending}
          isError={pos.isError}
          showInForce
        />
      </Section>

      {/* The factory's half of the PO: accepting it, or pushing back on it.
          Both facts lived on the order row and appeared on no screen between
          the board's flags and the drawer, so the stage the nine-step bar
          calls "PO acknowledged" had no section at all. */}
      <Section title="Supplier acceptance">
        <SupplierAcceptance order={order} />
      </Section>

      <Section title="Samples">
        {runs.length === 0 ? (
          <p className="order-page-muted">
            No sample runs yet. One is opened automatically when the factory accepts the PO.
          </p>
        ) : (
          runs.map((run) => (
            <div key={run.id} className="order-page-run">
              <div className="order-page-run-head">
                <strong>{run.title}</strong>
                <Badge tone={runTone(run.status)}>{runLabel(run.status)}</Badge>
                <span className="order-page-muted">{run.created_at.slice(0, 10)}</span>
                {!canReview ? null : (
                  <Button variant="secondary" size="sm" onClick={() => setOpenSubmission(run)}>
                    Review
                  </Button>
                )}
              </div>
              {/* The components and their photos, in place. This is the run —
                  reading it should not need a second click. Approved, it
                  carries the stamp and the watermark. */}
              <SampleApproval
                status={run.status}
                decidedBy={run.decided_by}
                decidedAt={run.decided_at}
              >
                <SupplierSampleSlots submissionId={run.id} canUpload={false} />
              </SampleApproval>
            </div>
          ))
        )}
      </Section>

      <Section title="Production">
        <ProductionStatus order={order} />
      </Section>

      <Section title="Shipping">
        <ShippingStatus order={order} />
      </Section>

      {order.date_delivered !== null || order.status === 'Closed' ? null : (
        <Section title="Delivery">
          <MarkDelivered order={order} />
        </Section>
      )}

      {/* Kyu, 22 Sep: the supplier bills about two weeks after the order, so
          the bill sits where it happens in the story, after the goods have
          gone out. Profit and margin are worked out here from it. */}
      <Section title="Supplier bill">
        <SupplierBill order={order} items={items} canEdit={canEdit} />
      </Section>

      {/* Optional by design. An order needs a project only when there is
          real follow-on work — design revisions, deliverables, coordination.
          Most orders need none, which is why this creates nothing on its own
          and sits down here rather than at the top. */}
      <Section title="Linked Project">
        <LinkedProjectBlock order={order} canEdit={canEdit} />
      </Section>

      {/* The measurement, after the thing it measures. */}
      <Section title="Time in each stage">
        <StageTimeline events={stages.data ?? []} today={todayNow()} />
      </Section>

      <Section title="History">
        {activity.isError ? (
          <InlineError>History could not be loaded.</InlineError>
        ) : (
          <ul className="order-page-activity">
            {sortActivity((activity.data ?? []).map(describeActivity)).map((line) => (
              <li key={line.id}>
                <span className="order-page-muted">{line.createdAt.slice(0, 10)}</span>
                <span>{line.sentence}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Last, and quiet. Deleting a job is not a step in it — putting the
          control anywhere in the run of sections would make it look like one,
          and the same reasoning already puts the project archive at the foot
          of its own page. */}
      <DangerZone order={order} />

      {!reviewingPo ? null : (
        <SendPoDialog order={order} items={items} onClose={() => setReviewingPo(false)} />
      )}

      {openSubmission === null ? null : (
        <SubmissionDialog
          submission={openSubmission}
          organizationName={supplier.data?.organizationName ?? ''}
          onClose={() => setOpenSubmission(null)}
        />
      )}
    </div>
  );
}

/**
 * The factory's half of the PO: accepting it, or pushing back on it.
 *
 * Two facts that lived on the order row and appeared nowhere between the
 * board's flags and the drawer. "PO acknowledged" is a step on the nine-step
 * bar, and until now the page jumped straight from the PO to the samples.
 *
 * The revision note is shown once raised and KEPT after it is answered —
 * revisionSummary says which. The conversation is why a target date moved, and
 * hiding it once handled leaves the next person with no idea.
 */
function SupplierAcceptance({ order }: { order: CgOrder }) {
  const update = useUpdateCgOrder();
  const acknowledge = useAcknowledgeSupplierRevision();
  const [error, setError] = useState('');
  const summary = revisionSummary(order);
  const outstanding = revisionOutstanding(order);
  // The factory's own promise, running from the moment they accepted, and
  // frozen at the moment the samples were approved. See sla.ts for why the
  // fourth argument is the production clock.
  const sla = slaRemaining(
    order.sample_clock_started_at,
    order.sample_due_days,
    todayNow(),
    order.production_clock_started_at,
  );

  return (
    <div className="order-page-stack">
      <dl className="order-page-facts">
        <dt>Accepted by the factory</dt>
        <dd>
          {order.supplier_confirmed_at === null
            ? 'Not yet — the PO is with them'
            : order.supplier_confirmed_at.slice(0, 10)}
        </dd>
        <dt>Acknowledged by TAP</dt>
        <dd>{order.date_acknowledged ?? '—'}</dd>
        {order.sample_clock_started_at === null ? null : (
          <>
            <dt>Sample window</dt>
            <dd>
              {sla.label}
              <span className="order-page-muted">
                {' '}
                · {order.sample_due_days} days from acceptance
              </span>
            </dd>
          </>
        )}
      </dl>

      {/* Only once the PO is actually out. Before that there is nothing to
          acknowledge, and a live button would invite stamping a date on a job
          the factory has not been sent. */}
      {order.date_acknowledged === null && order.date_po_sent !== null ? (
        <div className="order-page-deliver">
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() => {
              setError('');
              update.mutate(
                { orderId: order.id, update: { dateAcknowledged: todayIso() } },
                { onError: (cause) => setError(cgErrorMessage(cause.message)) },
              );
            }}
          >
            {update.isPending ? 'Recording…' : 'Mark acknowledged'}
          </Button>
        </div>
      ) : null}

      {summary === '' ? null : (
        <div className={outstanding ? 'order-page-pushback' : 'order-page-pushback-done'}>
          <p>
            <strong>{summary}</strong>
          </p>
          <p className="order-page-quote">“{order.supplier_revision_note}”</p>
          {!outstanding ? null : (
            <Button
              variant="secondary"
              size="sm"
              disabled={acknowledge.isPending}
              onClick={() => {
                setError('');
                acknowledge.mutate(order.id, {
                  onError: (cause) => setError(cgErrorMessage(cause.message)),
                });
              }}
            >
              {acknowledge.isPending ? 'Recording…' : 'We have answered this'}
            </Button>
          )}
        </div>
      )}

      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}

/**
 * The full run, which produces no artefact by design.
 *
 * Kyu ruled it: the factory tags ready-to-ship and shipped, and nothing
 * records what was built in between. So this section is thin on purpose — but
 * a stage with no section at all is different from a stage with nothing in it,
 * and the second is the honest one. What IS here is the promise: a production
 * window running from the moment the samples were approved, and the factory's
 * own signal that they are done.
 */
function ProductionStatus({ order }: { order: CgOrder }) {
  const sla = slaRemaining(
    order.production_clock_started_at,
    order.production_due_days,
    todayNow(),
  );

  return (
    <dl className="order-page-facts">
      <dt>Production window</dt>
      <dd>
        {order.production_clock_started_at === null ? (
          <span className="order-page-muted">Starts when the samples are approved</span>
        ) : (
          <>
            {sla.label}
            <span className="order-page-muted"> · {order.production_due_days} days agreed</span>
          </>
        )}
      </dd>
      <dt>Factory says ready</dt>
      <dd>
        {order.supplier_ready_for_shipment ? (
          <>
            <Badge tone="success">Ready to ship</Badge>
            {order.supplier_ready_at === null ? null : (
              <span className="order-page-muted"> {order.supplier_ready_at.slice(0, 10)}</span>
            )}
          </>
        ) : (
          <span className="order-page-muted">Not yet</span>
        )}
      </dd>
      {order.supplier_ready_note.trim() === '' ? null : (
        <>
          <dt>Their note</dt>
          <dd className="order-page-quote">“{order.supplier_ready_note}”</dd>
        </>
      )}
    </dl>
  );
}

/**
 * Leaving the factory: the date, the courier and the number to follow it with.
 *
 * Tracking used to sit in the summary facts at the top, which is where you
 * look for what a job IS rather than where it has got to. It belongs to this
 * stage, and the stage had no section.
 */
function ShippingStatus({ order }: { order: CgOrder }) {
  const update = useUpdateCgOrder();
  const [error, setError] = useState('');
  const label = trackingLabel(order.courier_name, order.tracking_number);
  const url = trackingLink(order.courier_name, order.tracking_number, order.tracking_url);

  return (
    <div className="order-page-stack">
      <dl className="order-page-facts">
        <dt>Shipped</dt>
        <dd>{order.date_shipped ?? '—'}</dd>
        <dt>Tracking</dt>
        <dd>
          {/* A link where we can build one — the useful half of a courier
              integration, at none of the cost. Plain text when we cannot: a
              guessed URL that 404s on a courier's site reads as the shipment
              being lost. */}
          {label === '' ? (
            <span className="order-page-muted">Nothing recorded. Add it in Quick edit.</span>
          ) : url === null ? (
            label
          ) : (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {label} ↗
            </a>
          )}
        </dd>
      </dl>

      {order.date_shipped === null ? (
        <div className="order-page-deliver">
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() => {
              setError('');
              update.mutate(
                { orderId: order.id, update: { dateShipped: todayIso(), status: 'Shipped' } },
                { onError: (cause) => setError(cgErrorMessage(cause.message)) },
              );
            }}
          >
            {update.isPending ? 'Recording…' : 'Mark shipped'}
          </Button>
          <p className="order-page-muted">Stamps today and moves the order to Shipped.</p>
        </div>
      ) : null}

      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}

/**
 * Client approval: the certificate, or the reason there isn't one.
 *
 * Kyu, on order #1038: "its past activities are messed up" — and the section
 * he was looking at was completely blank. A heading, a rule, and nothing at
 * all. CertificateLink renders null when there is no certificate, which is
 * correct for a button and wrong for a section: every other section on this
 * page says what it means to be empty, and a box that says nothing reads as a
 * page that failed to load rather than a fact about the order.
 *
 * There are two ways to have no certificate and they need different words.
 *
 * BEFORE the gate, the order is waiting on the client and the remedy is a
 * review link. That remedy only ever existed inside the drawer — the screen
 * Kyu asked us to stop landing on — so the one action that unblocks an order
 * was missing from the page that replaced it. It is here now.
 *
 * PAST the gate with no certificate is a state this system cannot produce:
 * cg_orders_guard_client_approval refuses the move. So the honest reading is
 * that the order arrived another way — imported from the tracker sheet, or
 * moved before the gate existed — and offering to chase a signature for a job
 * that shipped in July would be advice nobody should follow. It says what is
 * true and stops.
 */
function ClientApproval({
  order,
  certificate,
  onError,
}: {
  order: CgOrder;
  certificate: Certificate | null;
  onError: (message: string) => void;
}) {
  const statuses = useCgStatuses();
  const token = useOrderReviewToken(order.id);

  // The certificate case is answered before clientApprovalState is consulted
  // only because this one needs the object, not the verdict. The state
  // function still owns the other two, and its test covers all three.
  if (certificate !== null) {
    return <CertificateLink certificate={certificate} />;
  }

  const state = clientApprovalState(order.status, false, statuses.data ?? []);
  if (state === 'imported') {
    return (
      <p className="order-page-muted">
        No sign-off certificate is on file, and this order has already moved past the approval
        stage. Orders created here cannot do that — the approval gate refuses it — so this one was
        almost certainly imported from the tracker sheet or moved before the gate existed. Its
        earlier history lives outside the Hub.
      </p>
    );
  }

  return (
    <>
      <p className="order-page-muted">
        The client has not signed this order off yet. Send them the review link below — they approve
        each component on it, and the certificate appears here the moment they do.
      </p>
      <ReviewLinkPanel orderId={order.id} live={token.data ?? null} onError={onError} />
    </>
  );
}

/**
 * Recording that it arrived.
 *
 * A date somebody types, because Kyu ruled out a courier feed and was right:
 * an integration per carrier for a handful of orders a quarter, with a
 * background job that goes quiet without anybody noticing. A typed date is
 * more honest — it is obvious when it is missing.
 *
 * Defaults to today and is editable, because a courier confirmation arriving
 * on Monday for a Friday delivery is the normal case rather than the
 * exception. The database refuses a future date and one before the order
 * shipped.
 */
function MarkDelivered({ order }: { order: CgOrder }) {
  const mark = useMarkDelivered();
  // todayIso, not todayNow. todayNow returns a full ISO timestamp, which an
  // <input type="date"> cannot parse — so the field rendered EMPTY while the
  // state held a timestamp, and "Mark delivered" submitted a date the person
  // never saw. Kyu caught it by pressing the button on purpose without
  // touching the picker.
  const [on, setOn] = useState(todayIso());
  const [error, setError] = useState('');

  return (
    <div className="order-page-deliver">
      <Input
        label="Delivered on"
        type="date"
        value={on}
        onChange={(event) => setOn(event.target.value)}
      />
      {/* Belt as well as braces. The default above now populates the field,
          but a person can still clear it — and submitting an empty date sent
          `on: ''`, which the RPC read as "no date given" and silently stamped
          TODAY. Kyu: "I intentionally clicked Mark Delivered without picking a
          date... it should force us to pick a calendar date." A recorded
          delivery date nobody chose is worse than a refused click. */}
      <Button
        disabled={mark.isPending || on === ''}
        onClick={() => {
          setError('');
          mark.mutate({ orderId: order.id, on }, { onError: (cause) => setError(cause.message) });
        }}
      >
        {mark.isPending ? 'Recording…' : 'Mark delivered'}
      </Button>
      {on !== '' ? null : <p className="order-page-muted">Pick the date it arrived first.</p>}
      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}

/**
 * Deleting the job, from the page that is now the way into one.
 *
 * Kyu: "cannot delete an order anymore, button is gone." It was never removed
 * — it is in the drawer's ⋮ menu, and the drawer stopped being where anybody
 * lands. Rather than leaving it reachable only through a menu inside a form
 * somebody opens for other reasons, it gets a home of its own here.
 *
 * Two steps, and the reason is asked BEFORE the refusal rather than after it.
 * The database requires one for an order a client has signed off, and asking
 * on the way through means it is written while somebody still remembers it
 * instead of being retyped to get past an error.
 */
function DangerZone({ order }: { order: CgOrder }) {
  const remove = useDeleteCgOrder();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  return (
    <section className="order-page-danger">
      {!confirming ? (
        <button
          type="button"
          className="field-manager-danger-link"
          onClick={() => setConfirming(true)}
        >
          Delete this order
        </button>
      ) : (
        <div className="order-page-stack">
          <p>
            This removes {order.order_no} ({order.client}) and its line items. The activity log
            keeps its history. Sample runs holding no photos go with it; any holding photos are
            kept.
          </p>
          <Input
            label="Reason"
            value={reason}
            placeholder="Why is this being deleted?"
            onChange={(event) => setReason(event.target.value)}
            hint="Required for an order the client has signed off. Recorded either way."
          />
          <div className="order-page-deliver">
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                setError('');
                remove.mutate(
                  { orderId: order.id, reason },
                  {
                    // Back to the list, because the page this was pressed on
                    // is about to describe a row that no longer exists.
                    onSuccess: () => void navigate('/cg-orders'),
                    onError: (cause) => setError(cgErrorMessage(cause.message)),
                  },
                );
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete it'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
          {error === '' ? null : <InlineError>{error}</InlineError>}
        </div>
      )}
    </section>
  );
}

/**
 * The order's project, if it has one — and the two ways to give it one.
 *
 * Nothing here runs automatically. Signing off an order used to create a
 * project every time, which is what filled the Projects tab with records
 * nobody asked for; that was removed, and a guard test keeps it removed.
 */
function LinkedProjectBlock({ order, canEdit }: { order: CgOrder; canEdit: boolean }) {
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState('');
  const createForOrder = useCreateProjectForOrder();
  const linkExisting = useLinkOrderToProject();
  const projects = useLinkableProjects(picking);
  const busy = createForOrder.isPending || linkExisting.isPending;

  if (order.project_id !== null && order.project_id !== '') {
    return (
      <p className="order-page-linked-project">
        <Link to={`/projects/${order.project_id}`}>Open the linked project</Link>
      </p>
    );
  }

  if (!canEdit) {
    return <p className="order-page-muted">No project is linked to this order.</p>;
  }

  return (
    <div className="order-page-linked-project">
      <p className="order-page-muted">
        No project yet. Add one only if this order needs work tracked beyond fulfilment — design
        revisions, deliverables, internal coordination.
      </p>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {picking ? (
        <div className="order-page-link-picker">
          <label className="order-page-link-label" htmlFor="link-existing-project">
            Existing project
          </label>
          <select
            id="link-existing-project"
            className="ui-select"
            value={chosen}
            onChange={(event) => setChosen(event.target.value)}
          >
            <option value="">Choose a project…</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={busy || chosen === ''}
            onClick={() => {
              setError('');
              linkExisting.mutate(
                { orderId: order.id, projectId: chosen },
                {
                  onSuccess: () => setPicking(false),
                  onError: (cause) => setError(linkErrorMessage(cause.message)),
                },
              );
            }}
          >
            Link it
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setPicking(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="order-page-link-actions">
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              setError('');
              createForOrder.mutate(
                { orderId: order.id },
                { onError: (cause) => setError(linkErrorMessage(cause.message)) },
              );
            }}
          >
            {createForOrder.isPending ? 'Creating…' : 'Create linked Project'}
          </Button>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPicking(true)}>
            Link an existing project
          </Button>
        </div>
      )}
    </div>
  );
}

/** One section of the case file. A heading and a rule, so the page has seams. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="order-page-section">
      <h3 className="order-page-section-title">{title}</h3>
      {children}
    </section>
  );
}

function HandoffBlock({ handoff }: { handoff: SupplierHandoff }) {
  const links = useHandoffLinks(handoff.id);
  const acknowledge = useAcknowledgeHandoff();
  return (
    <div className="order-page-handoff">
      <div className="order-page-run-head">
        <strong>{handoff.title}</strong>
        {handoff.acknowledged_at === null ? (
          <Badge tone="warning">Awaiting the factory</Badge>
        ) : (
          <Badge tone="success">Acknowledged {handoff.acknowledged_at.slice(0, 10)}</Badge>
        )}
      </div>
      {handoff.tags.trim() === '' ? null : <p className="order-page-muted">Tags: {handoff.tags}</p>}
      {handoff.notes.trim() === '' ? null : <p>{handoff.notes}</p>}
      {handoff.ack_note.trim() === '' ? null : (
        <p className="order-page-muted">Factory said: “{handoff.ack_note}”</p>
      )}
      {(links.data ?? []).length === 0 ? null : (
        <ul className="order-page-links">
          {(links.data ?? []).map((link) => (
            <li key={link.id}>
              <a href={link.url} target="_blank" rel="noreferrer">
                {link.name.trim() === '' ? link.url : link.name}
              </a>
            </li>
          ))}
        </ul>
      )}
      {/* Deliberately not offered to TAP. Acknowledging is the FACTORY saying
          they have read it, and a button here would let us say it for them. */}
      {acknowledge.isError ? <InlineError>{acknowledge.error.message}</InlineError> : null}
    </div>
  );
}

function runTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'approved') return 'success';
  if (status === 'revision_requested') return 'danger';
  if (status === 'submitted') return 'warning';
  return 'neutral';
}

function runLabel(status: string): string {
  if (status === 'approved') return 'Approved for production';
  if (status === 'revision_requested') return 'Revision requested';
  if (status === 'submitted') return 'Awaiting TAP review';
  if (status === 'on_hold') return 'On hold';
  return status;
}

/**
 * The client's logo: one per order, replaceable, sent to the factory wherever
 * the approved artwork goes.
 *
 * Deliberately NOT a slot on the approval panel. The four components there
 * are things the client decides about; this is a file the client hands over.
 * Putting it beside them would invite the question "why isn't this approved",
 * to which the honest answer is that nothing about it is anyone's decision.
 */
function ClientLogoBlock({ orderId }: { orderId: string }) {
  const logo = useOrderClientLogo(orderId);
  const setLogo = useSetOrderClientLogo();
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState('');

  if (logo.isPending) {
    return <Skeleton lines={1} label="Loading the client logo" />;
  }

  const current = logo.data ?? null;

  return (
    <div className="order-page-logo">
      {current === null ? (
        <p className="order-page-muted">
          No logo yet. The factory sees it beside the approved artwork, on the PO and on their job
          page — so add it before the PO goes out.
        </p>
      ) : (
        <p className="order-page-muted">
          <a href={current.url} target="_blank" rel="noreferrer" className="file-name">
            Client logo
          </a>{' '}
          · v{current.version}
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf,.ai,.eps,.svg"
        className="file-input-hidden"
        aria-label={current === null ? 'Choose the client logo' : 'Choose a replacement logo'}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked) {
            setProblem('');
            setLogo.mutate(
              { orderId, file: picked },
              { onError: (cause) => setProblem(cgErrorMessage(cause.message)) },
            );
          }
          // So the same file can be picked again after a failure.
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={setLogo.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {setLogo.isPending ? 'Uploading…' : current === null ? 'Upload logo' : 'Replace logo'}
      </Button>
      {problem === '' ? null : <InlineError>{problem}</InlineError>}
    </div>
  );
}

/**
 * One of the two optional attachment slots.
 *
 * Kyu: "Treat these as additional optional attachment slots." So nothing is
 * required and nothing is blocked — an empty slot is a normal state, shown
 * rather than hidden so a missing file is visible before the PO goes out.
 *
 * The NAME is required when a file goes in, and that is the one thing this
 * adds over a bare upload button. A slot called "extra_1" arriving at the
 * factory tells Jen nothing, and a file with no explanation is exactly what
 * WhatsApp was already providing.
 */
function ReferenceSlot({
  orderId,
  kind,
  ordinal,
}: {
  orderId: string;
  kind: ReferenceKind;
  ordinal: number;
}) {
  const files = useOrderReferenceFiles(orderId);
  const setFile = useSetOrderReferenceFile();
  const clearFile = useClearOrderReferenceFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState('');
  const [problem, setProblem] = useState('');

  if (files.isPending) {
    return <Skeleton lines={1} label={`Loading attachment ${String(ordinal)}`} />;
  }

  const current = (files.data ?? []).find((file) => file.kind === kind) ?? null;

  if (current !== null) {
    return (
      <div className="order-page-logo">
        <p className="order-page-muted">
          <a href={current.url} target="_blank" rel="noreferrer" className="file-name">
            {current.label}
          </a>{' '}
          · v{current.version}
        </p>
        <Button
          variant="ghost"
          size="sm"
          disabled={clearFile.isPending}
          onClick={() => {
            setProblem('');
            clearFile.mutate(
              { orderId, kind },
              { onError: (cause: Error) => setProblem(cgErrorMessage(cause.message)) },
            );
          }}
        >
          {clearFile.isPending ? 'Removing…' : 'Remove'}
        </Button>
        {problem === '' ? null : <InlineError>{problem}</InlineError>}
      </div>
    );
  }

  return (
    <div className="order-page-logo">
      <Input
        label={`Attachment ${String(ordinal)} — what is it?`}
        value={label}
        placeholder="Packaging print file"
        onChange={(event) => setLabel(event.target.value)}
      />
      <input
        ref={inputRef}
        type="file"
        className="file-input-hidden"
        aria-label={`Choose attachment ${String(ordinal)}`}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked) {
            setProblem('');
            setFile.mutate(
              { orderId, kind, label: label.trim(), file: picked },
              {
                onSuccess: () => setLabel(''),
                onError: (cause: Error) => setProblem(cgErrorMessage(cause.message)),
              },
            );
          }
          // So the same file can be picked again after a failure.
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        // Named first, then chosen. The database refuses an unnamed slot, and
        // being refused after picking a file is a worse way to learn that.
        disabled={setFile.isPending || label.trim() === ''}
        onClick={() => inputRef.current?.click()}
      >
        {setFile.isPending ? 'Uploading…' : 'Upload file'}
      </Button>
      {problem === '' ? null : <InlineError>{problem}</InlineError>}
    </div>
  );
}

/**
 * Setting the Shopify order number.
 *
 * The reason is required by the database, not by this form, and it is the
 * point rather than paperwork: nothing else in the Hub records that this
 * number ever changed, so without it a corrected order is indistinguishable
 * from one that was always right.
 */
function ShopifyOrderNo({ order }: { order: CgOrder }) {
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState(order.order_no);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const save = useSetShopifyOrderNo(order.id);

  function submit() {
    setError('');
    save.mutate(
      { orderNo: next, reason },
      {
        onSuccess: () => {
          setOpen(false);
          setReason('');
        },
        // Straight from the database's own refusal, mapped to a sentence.
        // The form stays open holding it: closing would take the explanation
        // away with the thing that caused it.
        onError: (cause: Error) => setError(shopifyOrderNoError(cause.message)),
      },
    );
  }

  // Unconfirmed is not "not done yet", it is a block: cg_send_po_to_supplier
  // refuses while it is false (20270109000100). So the closed state says what
  // is stopping the PO rather than offering an optional tidy-up.
  const confirmed = order.order_no_confirmed;

  if (!open) {
    return (
      <p className={`order-page-shopify${confirmed ? '' : ' order-page-shopify--unconfirmed'}`}>
        <Button
          variant={confirmed ? 'secondary' : 'primary'}
          size="sm"
          onClick={() => {
            setNext(order.order_no);
            setError('');
            setOpen(true);
          }}
        >
          {confirmed ? 'Change Shopify order #' : 'Set Shopify order #'}
        </Button>
        <span className="task-panel-hint">
          {confirmed
            ? 'Confirmed against Shopify. It can still be corrected until a PO is issued, and not after.'
            : 'Not confirmed against Shopify yet, so the PO cannot go out. The Shopify order only exists once the client approves — put its number in here, or save the one already showing if it is right.'}
        </span>
      </p>
    );
  }

  // Saving the number unchanged CONFIRMS it, which is the ordinary case for an
  // order created after the client approved: the number was right all along
  // and somebody only has to say so. Making them type a different number first
  // would be asking for an error in order to clear a flag.
  const unchanged = next.trim() === order.order_no.trim();

  return (
    <div className="order-page-shopify order-page-shopify--open">
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <Input
        label="Shopify order #"
        value={next}
        placeholder="#1042"
        onChange={(event) => setNext(event.target.value)}
      />
      <Input
        label={unchanged ? 'Why you are confirming it' : 'Why it is changing'}
        value={reason}
        placeholder={
          unchanged ? 'Checked against Shopify' : 'Shopify assigned it after the client approved'
        }
        onChange={(event) => setReason(event.target.value)}
      />
      <span className="review-actions">
        <Button size="sm" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : unchanged ? 'Confirm' : 'Save'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setReason('');
            setError('');
          }}
        >
          Cancel
        </Button>
      </span>
    </div>
  );
}
